import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {environment} from '../../../../environments/environment';
import {UtmService} from './utm.service';
import {User, UserRoles} from '../../entities/user/user.entity';
import {Booking} from '../../entities/booking/booking.entity';
import {Service as MarketplaceService} from '../../entities/service/service.entity';
import {DateTime} from 'luxon';

interface TrackOptions {
  eventId?: string;
}

interface RegistrationParams {
  user?: User | null;
  email?: string | null;
  role?: string | null;
  source?: string;
  plan?: string | null;
  eventId?: string;
}

interface BookingParams {
  booking: Booking;
  user?: User | null;
  currency?: string;
  eventId?: string;
  roleHint?: string | null;
}

interface BookingIntentParams {
  bookingId: number;
  service: MarketplaceService | null;
  datetime: Date | DateTime | string;
  amount?: number | null;
  currency?: string;
  user?: User | null;
  eventId?: string;
  roleHint?: string | null;
  photographerId?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class MetaPixelService {
  private pixelBootstrapped = false;
  private configuredWithId = false;
  private readonly dedupe = new Set<string>();

  public constructor(
    private readonly utm: UtmService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) {}

  /**
   * Generic Meta Pixel event dispatcher with automatic UTM enrichment
   * and duplicate protection when an eventId is provided.
   */
  public track(eventName: string, params: Record<string, any> = {}, options: TrackOptions = {}): void {
    if (!this.isEnabled()) return;
    if (options.eventId && this.dedupe.has(options.eventId)) return;

    this.ensurePixelLoaded();
    const fbq = (window as any).fbq;
    if (typeof fbq !== 'function') return;

    const enriched = this.cleanPayload({
      ...params,
      ...this.getUtmProps(),
      ...this.getUtmProps('first'),
    });

    try {
      if (options.eventId) {
        fbq('track', eventName, enriched, {eventID: options.eventId});
        this.dedupe.add(options.eventId);
      } else {
        fbq('track', eventName, enriched);
      }
    } catch (error) {
      console.warn('Meta Pixel track failed', {eventName, enriched, error});
    }
  }

  /**
   * Fire Meta Pixel CompleteRegistration with consistent role metadata.
   */
  public trackRegistration(params: RegistrationParams): void {
    if (!this.isEnabled()) return;

    const roleMeta = this.extractRoleMeta(params.user, params.role);
    const eventId = params.eventId ?? this.buildEventId('registration', params.user?.id ?? params.email);

    this.track(
      'CompleteRegistration',
      this.cleanPayload({
        content_name: 'registration',
        plan: params.plan ?? null,
        source: params.source ?? 'unknown',
        user_id: params.user?.id ?? null,
        ...roleMeta,
      }),
      {eventId: eventId ?? undefined},
    );
  }

  /**
   * Fire Meta Pixel Schedule event for booking intent prior to payment.
   */
  public trackBookingIntent(params: BookingIntentParams): void {
    if (!this.isEnabled()) return;

    const roleMeta = this.extractRoleMeta(params.user, params.roleHint);
    const eventId = params.eventId ?? this.buildEventId('booking', params.bookingId);

    this.track(
      'Schedule',
      this.cleanPayload({
        booking_id: params.bookingId,
        content_type: 'service',
        content_ids: params.service?.id ? [params.service.id] : undefined,
        service_id: params.service?.id ?? null,
        service_name: params.service?.name ?? null,
        photographer_id: params.photographerId ?? null,
        scheduled_time: this.normaliseDate(params.datetime),
        value: params.amount ?? null,
        currency: params.currency ?? 'EUR',
        ...roleMeta,
      }),
      {eventId: eventId ?? undefined},
    );
  }

  /**
   * Fire Meta Pixel Purchase event once payment succeeds.
   */
  public trackPurchase(params: BookingParams): void {
    if (!this.isEnabled()) return;

    const booking = params.booking;
    const roleMeta = this.extractRoleMeta(params.user, params.roleHint);
    const eventId = params.eventId ?? this.buildEventId('purchase', booking.id);

    this.track(
      'Purchase',
      this.cleanPayload({
        booking_id: booking.id,
        content_type: 'service',
        content_ids: booking.service?.id ? [booking.service.id] : undefined,
        service_id: booking.service?.id ?? null,
        service_name: booking.service?.name ?? null,
        photographer_id: booking.service?.photographerId ?? null,
        value: booking.publicPrice ?? booking.price ?? null,
        currency: params.currency ?? 'EUR',
        status: booking.status ?? null,
        scheduled_time: this.normaliseDate(booking.datetime),
        ...roleMeta,
      }),
      {eventId: eventId ?? undefined},
    );
  }

  private isEnabled(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      Boolean(environment.metaPixel?.enabled) &&
      Boolean(environment.metaPixel?.id)
    );
  }

  private ensurePixelLoaded(): void {
    if (!this.isEnabled()) return;
    if (!this.pixelBootstrapped) {
      this.bootstrapSnippet();
      this.pixelBootstrapped = true;
    }

    if (!this.configuredWithId) {
      try {
        const fbq = (window as any).fbq;
        if (typeof fbq === 'function') {
          fbq('init', environment.metaPixel.id);
          this.configuredWithId = true;
        }
      } catch (error) {
        console.warn('Meta Pixel init failed', error);
      }
    }
  }

  private bootstrapSnippet(): void {
    const w = window as any;
    if (w.fbq) return;

    const fbq: any = function(this: any, ...args: any[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        (fbq.queue = fbq.queue || []).push(args);
      }
    };

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    w.fbq = fbq;
    w._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head?.appendChild(script);
    }
  }

  private getUtmProps(mode: 'last' | 'first' = 'last'): Record<string, any> {
    const bag = mode === 'last' ? this.utm.getLast() : this.utm.getFirst();
    if (!bag) return {};

    if (mode === 'last') {
      const {ts, ...rest} = bag;
      return rest;
    }

    const out: Record<string, any> = {};
    Object.entries(bag).forEach(([key, value]) => {
      if (key !== 'ts' && value) out[`first_${key}`] = value;
    });
    return out;
  }

  private extractRoleMeta(user?: User | null, fallbackRole?: string | null) {
    const roles = new Set<string>();
    user?.roles?.forEach(role => roles.add(role));
    if (!roles.size && fallbackRole) roles.add(fallbackRole);

    const isPhotographer = roles.has(UserRoles.photographer);
    const isCustomer = roles.has(UserRoles.customer);

    let userType: 'photographer' | 'customer' | 'hybrid' | 'unknown' = 'unknown';
    if (isPhotographer && isCustomer) userType = 'hybrid';
    else if (isPhotographer) userType = 'photographer';
    else if (isCustomer) userType = 'customer';

    return {
      user_type: userType,
      is_photographer: isPhotographer,
      is_customer: isCustomer,
    };
  }

  private normaliseDate(input: Date | DateTime | string): string | null {
    if (!input) return null;
    if (typeof input === 'string') return input;
    if (input instanceof Date) return input.toISOString();
    if ('toISO' in input) return (input as DateTime).toISO();
    return null;
  }

  private buildEventId(prefix: string, identifier?: string | number | null): string | null {
    if (identifier === undefined || identifier === null) return null;
    return `${prefix}-${identifier}`;
  }

  private cleanPayload(payload: Record<string, any>): Record<string, any> {
    const cleaned: Record<string, any> = {};
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      cleaned[key] = value;
    });
    return cleaned;
  }
}
